import type { GameState } from './gameState'
import type { ResourceStockpile } from './economyTypes'
import {
  getFacilityDefinition,
  isFacilityId,
  type FacilityId,
} from '../data/facilities'
import {
  PROTOTYPE_REGIONS,
  getPrototypeRegion,
  isActiveMajorPowerId,
  type ActiveMajorPowerId,
} from '../data/regions'
import { ACTIVE_MAJOR_POWER_IDS } from '../data/nations'
import { getNationPowerStatus } from './power'

export const PROTOTYPE_CONSTRUCTION_CAP = 2

export type RegionFacilityRecord = Record<string, FacilityId[]>

export interface ConstructionProject {
  id: number
  nationId: ActiveMajorPowerId
  regionId: string
  facilityId: FacilityId
  totalTurns: number
  turnsRemaining: number
}

export interface ConstructionState {
  nextProjectId: number
  facilities: RegionFacilityRecord
  queue: ConstructionProject[]
}

export type ConstructionBlockReason =
  | 'game-not-running'
  | 'region-not-found'
  | 'not-controlled'
  | 'no-slots'
  | 'queue-full'
  | 'insufficient-power'
  | 'insufficient-resources'

export interface ConstructionAvailability {
  allowed: boolean
  reason?: ConstructionBlockReason
}

export type QueueConstructionResult =
  | {
      queued: true
      state: GameState
      project: ConstructionProject
    }
  | {
      queued: false
      state: GameState
      reason: ConstructionBlockReason
    }

export interface ConstructionProgressResult {
  construction: ConstructionState
  completed: ConstructionProject[]
}

export function createInitialConstructionState(): ConstructionState {
  return {
    nextProjectId: 1,
    facilities: Object.fromEntries(
      PROTOTYPE_REGIONS.map((region) => [region.id, []]),
    ),
    queue: [],
  }
}

export function getConstructionAvailability(
  state: GameState,
  nationId: ActiveMajorPowerId,
  regionId: string,
  facilityId: FacilityId,
): ConstructionAvailability {
  if (state.status !== 'running') {
    return { allowed: false, reason: 'game-not-running' }
  }

  const region = getPrototypeRegion(regionId)
  if (!region) {
    return { allowed: false, reason: 'region-not-found' }
  }

  if (state.regionControllers[regionId] !== nationId) {
    return { allowed: false, reason: 'not-controlled' }
  }

  if (getOccupiedSlots(state.construction, regionId) >= region.slots) {
    return { allowed: false, reason: 'no-slots' }
  }

  if (
    state.construction.queue.filter(
      (project) => project.nationId === nationId,
    ).length >= PROTOTYPE_CONSTRUCTION_CAP
  ) {
    return { allowed: false, reason: 'queue-full' }
  }

  const facility = getFacilityDefinition(facilityId)
  const power = getNationPowerStatus(state, nationId)
  if (facility.powerDemand > power.available) {
    return { allowed: false, reason: 'insufficient-power' }
  }

  if (!canAfford(state.nations[nationId].economy.stockpile, facility.cost)) {
    return { allowed: false, reason: 'insufficient-resources' }
  }

  return { allowed: true }
}

export function queueFacilityConstruction(
  state: GameState,
  nationId: ActiveMajorPowerId,
  regionId: string,
  facilityId: FacilityId,
): QueueConstructionResult {
  const availability = getConstructionAvailability(
    state,
    nationId,
    regionId,
    facilityId,
  )

  if (!availability.allowed) {
    return {
      queued: false,
      state,
      reason: availability.reason ?? 'game-not-running',
    }
  }

  const facility = getFacilityDefinition(facilityId)
  const project: ConstructionProject = {
    id: state.construction.nextProjectId,
    nationId,
    regionId,
    facilityId,
    totalTurns: facility.buildTurns,
    turnsRemaining: facility.buildTurns,
  }
  const nation = state.nations[nationId]

  return {
    queued: true,
    project,
    state: {
      ...state,
      nations: {
        ...state.nations,
        [nationId]: {
          ...nation,
          economy: {
            ...nation.economy,
            stockpile: subtractCost(nation.economy.stockpile, facility.cost),
          },
        },
      },
      construction: {
        ...state.construction,
        nextProjectId: state.construction.nextProjectId + 1,
        queue: [...state.construction.queue, project],
      },
    },
  }
}

export function progressConstruction(
  construction: ConstructionState,
): ConstructionProgressResult {
  const completed: ConstructionProject[] = []
  const pending: ConstructionProject[] = []
  const facilities = cloneFacilityRecord(construction.facilities)

  for (const project of construction.queue) {
    const progressed = {
      ...project,
      turnsRemaining: project.turnsRemaining - 1,
    }

    if (progressed.turnsRemaining <= 0) {
      completed.push(progressed)
      facilities[project.regionId] = [
        ...(facilities[project.regionId] ?? []),
        project.facilityId,
      ]
    } else {
      pending.push(progressed)
    }
  }

  return {
    construction: {
      nextProjectId: construction.nextProjectId,
      facilities,
      queue: pending,
    },
    completed,
  }
}

export function getOccupiedSlots(
  construction: ConstructionState,
  regionId: string,
): number {
  const completed = construction.facilities[regionId]?.length ?? 0
  const pending = construction.queue.filter(
    (project) => project.regionId === regionId,
  ).length

  return completed + pending
}

export function isConstructionState(
  value: unknown,
): value is ConstructionState {
  if (!isRecord(value)) {
    return false
  }

  if (
    typeof value.nextProjectId !== 'number' ||
    !Number.isInteger(value.nextProjectId) ||
    value.nextProjectId < 1 ||
    !isRecord(value.facilities) ||
    !Array.isArray(value.queue)
  ) {
    return false
  }

  for (const region of PROTOTYPE_REGIONS) {
    const facilities = value.facilities[region.id]
    if (
      !Array.isArray(facilities) ||
      !facilities.every((facilityId) => isFacilityId(facilityId))
    ) {
      return false
    }
  }

  const ids = new Set<number>()

  for (const project of value.queue) {
    if (!isConstructionProject(project) || ids.has(project.id)) {
      return false
    }
    ids.add(project.id)
  }

  for (const region of PROTOTYPE_REGIONS) {
    if (getOccupiedSlots(value as unknown as ConstructionState, region.id) > region.slots) {
      return false
    }
  }

  for (const nationId of ACTIVE_MAJOR_POWER_IDS) {
    if (
      value.queue.filter(
        (project) =>
          isConstructionProject(project) && project.nationId === nationId,
      ).length > PROTOTYPE_CONSTRUCTION_CAP
    ) {
      return false
    }
  }

  return true
}

function canAfford(
  stockpile: ResourceStockpile,
  cost: ResourceStockpile,
): boolean {
  return (
    stockpile.money >= cost.money &&
    stockpile.metal >= cost.metal &&
    stockpile.fuel >= cost.fuel &&
    stockpile.electronics >= cost.electronics
  )
}

function subtractCost(
  stockpile: ResourceStockpile,
  cost: ResourceStockpile,
): ResourceStockpile {
  return {
    money: stockpile.money - cost.money,
    metal: stockpile.metal - cost.metal,
    fuel: stockpile.fuel - cost.fuel,
    electronics: stockpile.electronics - cost.electronics,
  }
}

function cloneFacilityRecord(
  facilities: RegionFacilityRecord,
): RegionFacilityRecord {
  return Object.fromEntries(
    PROTOTYPE_REGIONS.map((region) => [
      region.id,
      [...(facilities[region.id] ?? [])],
    ]),
  )
}

function isConstructionProject(value: unknown): value is ConstructionProject {
  if (!isRecord(value)) {
    return false
  }

  return (
    typeof value.id === 'number' &&
    Number.isInteger(value.id) &&
    value.id >= 1 &&
    isActiveMajorPowerId(value.nationId) &&
    typeof value.regionId === 'string' &&
    getPrototypeRegion(value.regionId) !== undefined &&
    isFacilityId(value.facilityId) &&
    typeof value.totalTurns === 'number' &&
    Number.isInteger(value.totalTurns) &&
    value.totalTurns >= 1 &&
    typeof value.turnsRemaining === 'number' &&
    Number.isInteger(value.turnsRemaining) &&
    value.turnsRemaining >= 1 &&
    value.turnsRemaining <= value.totalTurns
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
